// ============================================
// Image Module (ES6)
// 이미지 캐싱 및 UI
// ============================================

import { IMAGE_CACHE_KEY, IMAGE_CACHE_EXPIRY, IMAGE_MODEL_OPTIONS, DEFAULT_IMAGE_MODEL } from './config.js';
import { escapeHtml } from './utils.js';

// ============================================
// 이미지 캐싱
// ============================================

export function getCachedImage(url) {
  if (!url) return url;

  try {
    const cache = JSON.parse(localStorage.getItem(IMAGE_CACHE_KEY) || '{}');
    const cached = cache[url];

    if (cached && Date.now() - cached.timestamp < IMAGE_CACHE_EXPIRY) {
      return cached.dataUrl || url;
    }
  } catch (e) {
    console.warn('Image cache read error:', e);
  }

  cacheImageInBackground(url);
  return url;
}

export function cacheImageInBackground(url) {
  if (!url || url.startsWith('data:')) return;
  if (window.location.protocol === 'file:') return;

  const img = new Image();
  img.crossOrigin = 'anonymous';

  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const maxSize = 150;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      const cache = JSON.parse(localStorage.getItem(IMAGE_CACHE_KEY) || '{}');

      const keys = Object.keys(cache);
      if (keys.length >= 50) {
        const oldest = keys.sort((a, b) => cache[a].timestamp - cache[b].timestamp)[0];
        delete cache[oldest];
      }

      cache[url] = {
        dataUrl: dataUrl,
        timestamp: Date.now()
      };

      localStorage.setItem(IMAGE_CACHE_KEY, JSON.stringify(cache));
    } catch (e) {
      console.warn('Image cache error:', e);
    }
  };

  img.onerror = () => { };
  img.src = url;
}

export function clearOldImageCache() {
  try {
    const cache = JSON.parse(localStorage.getItem(IMAGE_CACHE_KEY) || '{}');
    const now = Date.now();
    let cleaned = false;

    for (const url in cache) {
      if (now - cache[url].timestamp > IMAGE_CACHE_EXPIRY) {
        delete cache[url];
        cleaned = true;
      }
    }

    if (cleaned) {
      localStorage.setItem(IMAGE_CACHE_KEY, JSON.stringify(cache));
    }
  } catch (e) {
    localStorage.removeItem(IMAGE_CACHE_KEY);
  }
}

// ============================================
// 이미지 모델 설정
// ============================================

export function getSelectedImageModel() {
  const savedModel = localStorage.getItem('selectedImageModel') || DEFAULT_IMAGE_MODEL;
  return IMAGE_MODEL_OPTIONS.find(m => m.id === savedModel) || IMAGE_MODEL_OPTIONS[0];
}

export function setSelectedImageModel(modelId) {
  localStorage.setItem('selectedImageModel', modelId);
}

// ============================================
// 이미지 모달
// ============================================

export function showImageModal(imageUrl, name = '점술가') {
  const safeName = escapeHtml(name);

  const modal = document.createElement('div');
  modal.className = 'modal-overlay active';
  modal.id = 'imageViewModal';
  modal.style.zIndex = '10100';
  modal.onclick = (e) => {
    if (e.target === modal) {
      closeImageViewModal();
    }
  };
  modal.innerHTML = `
    <div class="image-view-modal">
      <button class="modal-close" data-action="close">×</button>
      <img src="${imageUrl}" alt="${safeName}" style="max-width: 100%; max-height: 80vh; border-radius: 12px;">
      <p style="text-align: center; margin-top: 12px; color: var(--text-secondary);">${safeName}</p>
    </div>
  `;
  document.body.appendChild(modal);

  // 이벤트 위임 (닫기 버튼)
  modal.addEventListener('click', (e) => {
    if (e.target.closest('[data-action="close"]')) {
      closeImageViewModal();
    }
  });

  if (window.ModalManager) {
    window.ModalManager.push('imageViewModal');
  }
}

export function closeImageViewModal() {
  const modal = document.getElementById('imageViewModal');
  if (modal) {
    modal.remove();
  }
  if (window.ModalManager) {
    window.ModalManager.remove('imageViewModal', true);
  }
}

// ============================================
// 초기화
// ============================================

clearOldImageCache();

// ============================================
// 하위 호환성 - window 객체에 노출 (필수만 유지)
// ============================================

window.showImageModal = showImageModal;
window.closeImageViewModal = closeImageViewModal;
